// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import PageContext from 'components/Page/context'
import { usePrevious } from 'hooks/utility'
import { useDispatchAction } from 'hooks/redux'
import { HeaderVariant, setHeaderVariant } from 'components/Layout/LayoutDucks'
import Condition from 'components/Condition'
import useStyles from './styles'

let currentHeaderIsCompact: boolean | undefined
let currentHeaderIsAlternative: boolean | undefined
let resetCurrentHeaderTypeTimeoutHandle = 0
let currentHeaderHeight = 0

interface IPageHeaderProps {
  backgroundImage?: string
  alternative?: boolean
  contentStyle?: React.CSSProperties
  compact?: boolean
  autoCompact?: boolean
  autoAlternative?: boolean
  className?: string
  wide?: boolean
  white?: boolean
  children?: React.ReactNode
  classes?: Partial<Record<string, string>>
}

function PageHeader(props: IPageHeaderProps) {
  const { classes } = useStyles()
  const {
    children,
    contentStyle,
    compact,
    autoCompact,
    alternative,
    autoAlternative,
    className,
    wide,
    white,
    backgroundImage,
    ...restProps
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const altBackdropRef = useRef<HTMLDivElement>(null)

  const setVariant = useDispatchAction((variant: HeaderVariant) => setHeaderVariant({ variant: variant }))
  const [isAlternative, setIsAlternative] = useState<boolean>(alternative)

  const { sidebar, sidebarMode } = React.useContext(PageContext)
  const prevAlternative = usePrevious(alternative)

  const getStyleName = () => {
    const styleNameList = [classes.header]

    if (compact || (autoCompact && currentHeaderIsCompact)) {
      styleNameList.push(classes.compact)
    }

    if (alternative || (autoAlternative && currentHeaderIsAlternative)) {
      styleNameList.push(classes.alternative)
    }

    if (className) {
      styleNameList.push(className)
    }

    if (white) {
      styleNameList.push(classes.white)
    }

    if (wide) {
      styleNameList.push('wide')
    }

    if (sidebar) {
      if (sidebarMode === 'column') {
        styleNameList.push(classes.sidebarColumn)
      }
    }

    return styleNameList.join(' ')
  }

  const actualizeBackdrop = useCallback(() => {
    const thisHeight = containerRef.current.getBoundingClientRect().height

    if (backdropRef.current.animate) {
      const shouldAnimateHeight = (currentHeaderHeight && thisHeight !== currentHeaderHeight)
      const shouldAnimateColor = (alternative !== currentHeaderIsAlternative && currentHeaderIsAlternative !== undefined)

      if (shouldAnimateHeight) {
        backdropRef.current.animate(
          [
            // This essentially animates the height change, only instead by using a vertical scaling transition, which is much faster.
            { transform: `scale3d(1, ${thisHeight > 0 ? (currentHeaderHeight / thisHeight) || 0 : 0}, 1)` },
            { transform: 'scale3d(1, 1, 1)' },
          ],
          {
            duration: 600,
            easing: 'ease',
          },
        )
      }

      if (shouldAnimateColor) {
        altBackdropRef.current.animate(
          [
            { opacity: currentHeaderIsAlternative ? 1 : 0 },
            { opacity: alternative ? 1 : 0 },
          ],
          {
            duration: 600,
            easing: 'ease',
          },
        )
      }
    }

    currentHeaderIsCompact = Boolean(compact)
    currentHeaderIsAlternative = Boolean(alternative)
    currentHeaderHeight = thisHeight
  }, [alternative, compact])

  useEffect(() => {
    window.clearTimeout(resetCurrentHeaderTypeTimeoutHandle)

    if (alternative && !prevAlternative) {
      setVariant('default')
      setIsAlternative(alternative)
    } else if (!alternative && prevAlternative) {
      setVariant('primary')
    }

    actualizeBackdrop()

    if (alternative) setVariant('default')

    return () => {
      window.clearTimeout(resetCurrentHeaderTypeTimeoutHandle)
      resetCurrentHeaderTypeTimeoutHandle = window.setTimeout(() => {
        currentHeaderHeight = 0
      }, 1000)

      if (isAlternative) {
        setVariant('primary')
      }
    }
  }, [actualizeBackdrop, alternative, isAlternative, prevAlternative, setVariant])

  return (
    <div {...restProps} className={getStyleName()} ref={containerRef}>
      <div className={classes.backdrop} ref={backdropRef}>
        <div className={cn(classes.altBackdrop, 'alt-backdrop')} ref={altBackdropRef} />
      </div>

      <Condition condition={Boolean(backgroundImage)}>
        <div className={classes.background} style={{ backgroundImage: `url(${backgroundImage})` }} />
        <div className={classes.backgroundOverlay} />
      </Condition>

      <div className={cn(classes.headerContent, 'header-content')} style={contentStyle}>
        {children}
      </div>
    </div>
  )
}

export default PageHeader
